import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useSWRConfig } from 'swr';
import { CircularProgress, useMediaQuery } from '@mui/material';
import { MyInfoState } from '@/recoil/atom';
import { PAGE_URL } from '@/constant/url';
import {
  APPROVAL_V2_URL,
  RETURN_REQUEST_REASON_MAX_LENGTH,
} from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { isRnWebView, RnMessage, sendToRN } from '@/utils/function';
import { useBookMark } from '@/hooks/apiHooks/approval';
import useApprovalView from '@/hooks/approval/useApprovalView';
import useApprovalLineMembers from '@/hooks/approval/useApprovalLineMembers';
import useWithdrawApproval from '@/hooks/approval/useWithdrawApproval';
import useRequestReturnApproval from '@/hooks/approval/useRequestReturnApproval';
import { getRequestReturnErrorMessage } from '@/utils/approval/returnRequestErrorMessage';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import useApprovalViewRelations from '@/hooks/approval/useApprovalViewRelations';
import useTranslateApproval from '@/hooks/approval/useTranslateApproval';
import {
  canRecallDocument,
  canRequestReturnDocument,
} from '@/utils/approval/authorOngoingActions';
import { canAddReferrer } from '@/utils/approval/referrerAddable';
import {
  isTranslatedAtom,
  isTranslatingAtom,
  translationResultAtom,
  approvalAiCarryOverAtom,
} from './atoms';
import TopStatusBar from './TopStatusBar';
import CategoryBreadcrumb from './CategoryBreadcrumb';
import StatusFormLabels from './StatusFormLabels';
import TitleMetaBar from './TitleMetaBar';
import TranslationStatusBar from './TranslationStatusBar';
import DocumentActions, {
  computeActionVisibility,
  isCurrentApproverMeOf,
} from './DocumentActions';
import useDocumentActionHandlers from './useDocumentActionHandlers';
import ApprovalLineSlot from './ApprovalLineSlot';
import OpinionSlot from './OpinionSlot';
import ReturnRequestNotice from './ReturnRequestNotice';
import AiSummarySlot from './AiSummarySlot';
import BodySlot from './BodySlot';
import AttachmentsSlot from './AttachmentsSlot';
import RelatedApprovalSlot from './RelatedApprovalSlot';
import CommentSlot from './CommentSlot';
import ReadersSlot from './ReadersSlot';
import BottomLabels from './BottomLabels';
import ReasonInputModal from './ReasonInputModal';
import AddReferrerModal from './AddReferrerModal';
import ImageZoomModal from './ImageZoomModal';
import { ImageZoomProvider } from './ImageZoomModal/context';
import { isNativeViewApprovalType } from '@/utils/approval/approvalAppTab';
import { canUseApprovalAiAssistant } from '@/utils/approval/aiAssistantAccess';
import AiAssistantHost from './AiAssistantPanel/AiAssistantHost';
import { isSelfApprovalAttendanceType, useAttendanceCancel } from './slots';
import useCardCompanyMail from './useCardCompanyMail';
import { Container, HiddenApprovalLineSpacer, StateFallback } from './styles';
import useHoverScrollbar from '@/components/Approval/@hooks/useHoverScrollbar';
import type { ApprovalViewAuthor, ApprovalViewProps } from './types';

const EMPTY_I18N_TEXT = { ko: null, en: null, zh: null };
/** 타입은 non-null 이지만 기안자 행이 없는 문서가 실제로 옴 — 화면째 죽는 대신 빈 값 표시 */
const EMPTY_AUTHOR: ApprovalViewAuthor = {
  intranetId: '',
  passportId: '',
  name: EMPTY_I18N_TEXT,
  employeeNumber: '',
  companyCode: '',
  companyName: EMPTY_I18N_TEXT,
  divisionCode: '',
  divisionName: EMPTY_I18N_TEXT,
  departmentCode: '',
  departmentName: '',
  positionCode: '',
  positionName: EMPTY_I18N_TEXT,
  departmentId: '',
  profileImageUrl: null,
};

const ApprovalView = ({
  documentId,
  apvType,
  onClose,
  onStatusChange,
  onEdit,
  onDecided,
  commentDirtyRef,
}: ApprovalViewProps) => {
  // 액션 핸들러는 3단계에서 사용. 본 단계에서는 props 받아두기만.
  void apvType;

  const { t } = useTranslation();
  const router = useRouter();
  const { showSuccess, showError } = useSnackbar();
  const { data, error, isLoading } = useApprovalView(documentId);
  const { getMemberState } = useApprovalLineMembers(documentId);
  const { mutate: mutateGlobal } = useSWRConfig();
  // 참조인 변경 후 단건 캐시 무효화 — 튜플 key(useApprovalView)만 깨면 편집 hydrate 용
  // 문자열 key(useApproval)가 stale 로 남아 재상신 시 추가 참조인이 누락된다.
  const invalidateDocumentCaches = useCallback(async () => {
    const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
    await mutateGlobal(
      (cacheKey) =>
        cacheKey === baseKey ||
        (Array.isArray(cacheKey) && cacheKey[0] === baseKey),
    );
  }, [mutateGlobal, documentId]);
  const {
    parents: relatedParents,
    isBookmark: relatedIsBookmark,
    isLoading: relatedIsLoading,
    mutate: mutateRelated,
  } = useApprovalViewRelations(documentId);

  const { execute: requestTranslation, cancel: cancelTranslation } =
    useTranslateApproval(documentId);

  const setTranslationResult = useSetAtom(translationResultAtom);
  const setIsTranslated = useSetAtom(isTranslatedAtom);
  const setIsTranslating = useSetAtom(isTranslatingAtom);
  useEffect(() => {
    return () => {
      setTranslationResult(null);
      setIsTranslated(false);
      setIsTranslating(false);
    };
  }, [documentId, setTranslationResult, setIsTranslated, setIsTranslating]);

  // 코멘트 영역 ref + TitleMetaBar 의 코멘트 아이콘 클릭 시 스크롤 트리거.
  const commentSectionRef = useRef<HTMLDivElement | null>(null);
  const handleScrollToComments = useCallback(() => {
    commentSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const [commentCount, setCommentCount] = useState(0);

  const commentRefreshRef = useRef<(() => Promise<void>) | null>(null);
  // AI 패널이 열려 있었는지 — 결재 직후 다음 문서에서 패널을 이어 열지 판단 (PORTAL-15735)
  const aiAssistantOpenRef = useRef(false);
  const setAiCarryOver = useSetAtom(approvalAiCarryOverAtom);
  const handleDecided = useCallback(
    (action: ApprovalDecisionAction) => {
      void commentRefreshRef.current?.();
      // 보류는 문서에 머무르므로 제외 — 승인·반려 뒤 이어 여는 다음 문서만 해당
      if (aiAssistantOpenRef.current && action !== 'hold') {
        setAiCarryOver({ from: documentId });
      }
      onDecided?.(action);
    },
    [documentId, onDecided, setAiCarryOver],
  );

  const printableRef = useRef<HTMLDivElement | null>(null);
  const hoverScrollbarHandlers = useHoverScrollbar();

  const handleToggleBookmark = useCallback(async () => {
    const current = relatedIsBookmark;
    const next = !current;
    await mutateRelated(
      (prev) => (prev ? { ...prev, isBookmark: next } : prev),
      { revalidate: false },
    );
    try {
      await useBookMark(documentId);
      if (next) {
        showSuccess({ message: t('즐겨찾기에 추가되었습니다.') });
      }
    } catch {
      await mutateRelated(
        (prev) => (prev ? { ...prev, isBookmark: current } : prev),
        { revalidate: false },
      );
      showError({ message: t('즐겨찾기 처리에 실패했습니다.') });
    }
  }, [relatedIsBookmark, mutateRelated, documentId, showError, showSuccess, t]);

  // 로그인 유저 정보 — DocumentActions/회수 노출 여부 계산용
  const myInfo = useAtomValue(MyInfoState);
  const myPassportId = myInfo?.passportId ?? '';

  // 하위 슬롯에도 그대로 내려가므로 여기서 한 번만 대체
  const author = data?.author ?? EMPTY_AUTHOR;
  const isAuthorMe = !!myPassportId && author.passportId === myPassportId;

  // 근태 자가결재 "품의 취소" — 즉시 취소는 문서가 X 로 바뀌므로 호스트 목록도 동기화
  const {
    canCancel: canCancelAttendance,
    cancelDisabled: cancelAttendanceDisabled,
    cancel: handleCancelAttendance,
  } = useAttendanceCancel({
    documentId,
    apvType: data?.type.code,
    status: data?.status,
    isAuthorMe,
    onCancelled: () => onStatusChange?.('X'),
  });

  const { canSendMail, sendMailDisabled, sendMail } = useCardCompanyMail({
    documentId,
    apvType: data?.type.code,
    status: data?.status,
  });

  // 액션 버튼 row 와 더보기 메뉴가 같은 판정을 공유 — 레거시도 편집/삭제를 두 곳에 노출
  const { showEdit, showDelete, showActionTrigger } = useMemo(() => {
    if (!data) {
      return { showEdit: false, showDelete: false, showActionTrigger: false };
    }
    const isAuthorMe = !!myPassportId && author.passportId === myPassportId;
    const isCurrentApproverMe = isCurrentApproverMeOf(
      data.approvers,
      data.currentApproverOrder,
      myPassportId,
      data.status,
    );
    return computeActionVisibility({
      status: data.status,
      isAuthorMe,
      isCurrentApproverMe,
      apvType: data.type.code,
    });
  }, [data, myPassportId, author.passportId]);
  const hasAnyDocAction = showEdit || showDelete || showActionTrigger;

  /**
   * AI 어시스턴트 패널 (PORTAL-15497). 문서를 볼 수 있으면 누구나 질문할 수 있고,
   * 반려 제안 카드의 버튼만 현재 결재 차례인 사용자에게 보인다. PC 는 우측 고정 패널, 모바일은 바텀시트.
   */
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  aiAssistantOpenRef.current = isAiAssistantOpen;
  const isMobileViewport = useMediaQuery('(max-width: 768px)');
  // 베타: 환경과 무관하게 V2 화면으로 열리는 양식 + 조직 코드가 FF137(IT담당) 산하인 사람만 (utils/approval/aiAssistantAccess).
  // 양식 기준은 뷰어가 V2 화면/레거시 iframe 을 고르는 조회 목록과 같다 — 작성은 근태 메뉴인 근태 4종도 포함 (PORTAL-15731).
  // 전면 오픈은 aiAssistantAccess 의 허용 목록을 비우면 된다.
  const canOpenAiAssistant =
    !!data &&
    isNativeViewApprovalType(data.type.code) &&
    canUseApprovalAiAssistant(myInfo);
  const canDecideFromAssistant = useMemo(
    () =>
      !!data &&
      data.status === 'P' &&
      isCurrentApproverMeOf(
        data.approvers,
        data.currentApproverOrder,
        myPassportId,
        data.status,
      ),
    [data, myPassportId],
  );
  const handleToggleAiAssistant = useCallback(
    () => setIsAiAssistantOpen((prev) => !prev),
    [],
  );
  const handleCloseAiAssistant = useCallback(
    () => setIsAiAssistantOpen(false),
    [],
  );
  // 패널은 문서가 바뀌어도 열림·닫힘을 그대로 둔다 — 열어 둔 채 다음 문서로 가면 계속 열려 있다 (PORTAL-15735).
  // 다음 문서를 화면을 새로 띄워 여는 호스트(모바일·앱 일부)는 상태가 사라지므로, 패널을 연 채 결재했다는
  // 표시를 남겨 두고 다른 문서로 처음 뜰 때 한 번 열고 지운다.
  const jotaiStore = useStore();
  useEffect(() => {
    const carry = jotaiStore.get(approvalAiCarryOverAtom);
    if (carry && carry.from !== documentId) {
      jotaiStore.set(approvalAiCarryOverAtom, null);
      setIsAiAssistantOpen(true);
    }
  }, [documentId, jotaiStore]);

  /** 상신 성공 후: 단건 캐시 재검증은 훅이 처리, 여기선 외부 목록만 진행중으로 동기화. */
  const handleSubmitted = useCallback(() => {
    onStatusChange?.('P');
  }, [onStatusChange]);

  /**
   * 문서삭제 성공 후: 모달 호스트는 리스트 동기화 후 닫기, 풀스크린 viewer 는 목록 이동.
   * 삭제 시 드래프트 코멘트 보존이 무의미하므로 commentDirtyRef 를 비워 close-guard 를 통과시킨다.
   * 앱 웹뷰는 포탈 목록으로 보내면 웹뷰 안에 갇히므로 웹뷰를 닫아 앱 목록으로 돌려보낸다.
   */
  const handleDeleted = useCallback(() => {
    onStatusChange?.('D');
    if (onClose) {
      if (commentDirtyRef) commentDirtyRef.current = null;
      onClose();
    } else if (isRnWebView()) {
      sendToRN(RnMessage.BACK);
    } else {
      router.push(PAGE_URL.WORK_APPROVAL);
    }
  }, [onStatusChange, onClose, router, commentDirtyRef]);

  const { handleEdit: handleEditDocument, handleDelete: handleDeleteDocument } =
    useDocumentActionHandlers({
      documentId,
      onEdit,
      onDeleted: handleDeleted,
    });

  /**
   * 기안자의 진행중 조치 노출 — 회수 / 반려요청. 판정과 상보 규칙은 utils 단일 출처.
   * 완료·반려·취소·임시저장 문서는 상태 조건에서 걸러진다(PORTAL-3406 재발 방지).
   */
  const canRecall = useMemo(
    () => (data ? canRecallDocument(data, myPassportId) : false),
    [data, myPassportId],
  );
  const canRequestReturn = useMemo(
    () => (data ? canRequestReturnDocument(data, myPassportId) : false),
    [data, myPassportId],
  );

  /**
   * 반려요청 사유 안내를 볼 사람 — 요청을 받은 현재 결재자와, 요청을 건 작성자 본인.
   *
   * 반려요청은 결재 액션이 아니라 "반려해 달라"는 부탁이고, 실제 반려는 현재 결재자가 한다.
   * 그래서 이미 결재를 마친 결재자·참조인·열람자에게는 노출하지 않는다.
   * 작성자를 포함하는 이유는, 요청이 걸려 있는지 본인이 확인할 자리가 달리 없기 때문이다.
   */
  const canSeeReturnRequest = useMemo(() => {
    if (!data) return false;
    if (!!myPassportId && author.passportId === myPassportId) return true;
    return isCurrentApproverMeOf(
      data.approvers,
      data.currentApproverOrder,
      myPassportId,
      data.status,
    );
  }, [data, myPassportId, author.passportId]);

  // 회수 사유 입력 모달 — 우상단 '회수' 버튼 + 더보기 메뉴 '회수' 항목 양쪽에서 연다.
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const { withdraw, isSubmitting: isWithdrawing } = useWithdrawApproval();
  const handleOpenWithdraw = useCallback(() => setIsWithdrawOpen(true), []);
  const handleCloseWithdraw = useCallback(() => setIsWithdrawOpen(false), []);

  /**
   * 참조인 추가 모달 (ReadersSlot 버튼 + 더보기 메뉴).
   * 노출: 하단 버튼·더보기 모두 공용 판정(canAddReferrer)을 쓴다 — P/H/C/R 허용,
   * security 9(비공개)와 임시저장만 차단. 반려 문서 하단 버튼 누락을 맞춘 것 (PORTAL-13928).
   */
  const [isAddReferrerOpen, setIsAddReferrerOpen] = useState(false);
  const handleOpenAddReferrer = useCallback(
    () => setIsAddReferrerOpen(true),
    [],
  );
  const handleCloseAddReferrer = useCallback(
    () => setIsAddReferrerOpen(false),
    [],
  );
  const canAddReferrerToDoc = useMemo(
    () => (data ? canAddReferrer(data) : false),
    [data],
  );
  const handleWithdraw = useCallback(
    async (reason: string) => {
      if (isWithdrawing) return; // 중복 클릭 방지 (모달 버튼도 disabled)
      try {
        await withdraw(documentId, reason);
        setIsWithdrawOpen(false);
        // 회수 = 반려(R)로 종결. 단건 캐시 무효화는 useWithdrawApproval 내부 처리.
        // 모달/리스트 호스트가 외부 목록을 반려 상태로 동기화.
        onStatusChange?.('R');
        showSuccess({ message: t('품의 회수가 완료되었습니다.') });
      } catch {
        // 모달은 열린 채 유지 — 사용자가 사유 수정 후 재시도 가능.
        showError({ message: t('회수에 실패했습니다.') });
      }
    },
    [
      isWithdrawing,
      withdraw,
      documentId,
      onStatusChange,
      showSuccess,
      showError,
      t,
    ],
  );

  // 반려요청 사유 입력 모달 — 회수와 같은 두 지점(우상단 버튼 + 더보기 메뉴)에서 연다.
  const [isReturnRequestOpen, setIsReturnRequestOpen] = useState(false);
  const { requestReturn, isSubmitting: isRequestingReturn } =
    useRequestReturnApproval();
  const handleOpenReturnRequest = useCallback(
    () => setIsReturnRequestOpen(true),
    [],
  );
  const handleCloseReturnRequest = useCallback(
    () => setIsReturnRequestOpen(false),
    [],
  );

  /**
   * 반려요청 전송 — 문서 상태는 그대로라 onStatusChange 로 외부 목록을 건드리지 않는다.
   * 요청 표식·사유는 훅의 캐시 무효화로 조회 화면에 반영된다.
   */
  const handleRequestReturn = useCallback(
    async (reason: string) => {
      if (isRequestingReturn) return; // 중복 클릭 방지 (모달 버튼도 disabled)
      try {
        await requestReturn(documentId, reason);
        setIsReturnRequestOpen(false);
        showSuccess({ message: t('반려요청이 완료되었습니다.') });
      } catch (error) {
        // 모달은 열린 채 유지 — 사용자가 사유 수정 후 재시도 가능.
        showError({ message: t(getRequestReturnErrorMessage(error)) });
      }
    },
    [isRequestingReturn, requestReturn, documentId, showSuccess, showError, t],
  );

  if (isLoading && !data) {
    return (
      <StateFallback>
        <CircularProgress color="inherit" />
      </StateFallback>
    );
  }

  if (error || !data) {
    return (
      <StateFallback>
        <span>{t('문서를 불러오지 못했습니다.')}</span>
      </StateFallback>
    );
  }

  return (
    <ImageZoomProvider>
      <TranslationStatusBar onCancel={cancelTranslation} />
      <TopStatusBar status={data.status} />
      <Container ref={printableRef} {...hoverScrollbarHandlers}>
        <CategoryBreadcrumb
          subject={data.subject}
          status={data.status}
          loading={relatedIsLoading}
          parents={relatedParents.map((p) => ({
            docId: p.docId,
            subject: p.subject,
          }))}
          canRecall={canRecall}
          onRecall={handleOpenWithdraw}
          canRequestReturn={canRequestReturn}
          onRequestReturn={handleOpenReturnRequest}
        />
        <StatusFormLabels status={data.status} type={data.type} />
        {data.isReturnRequested && canSeeReturnRequest && (
          <ReturnRequestNotice reason={data.returnReason} />
        )}
        <TitleMetaBar
          subject={data.subject}
          author={author}
          authoredAt={data.authoredAt}
          documentId={documentId}
          printableRef={printableRef}
          onRequestTranslation={requestTranslation}
          onScrollToComments={handleScrollToComments}
          commentCount={commentCount}
          noBottomBorder={hasAnyDocAction}
          canRecall={canRecall}
          onRecall={handleOpenWithdraw}
          canRequestReturn={canRequestReturn}
          onRequestReturn={handleOpenReturnRequest}
          canAddReferrer={canAddReferrerToDoc}
          onAddReferrer={handleOpenAddReferrer}
          canEditDocument={showEdit}
          onEditDocument={handleEditDocument}
          canDeleteDocument={showDelete}
          onDeleteDocument={handleDeleteDocument}
          canCancelAttendance={canCancelAttendance}
          cancelAttendanceDisabled={cancelAttendanceDisabled}
          onCancelAttendance={handleCancelAttendance}
          canSendCardCompanyMail={canSendMail}
          sendCardCompanyMailDisabled={sendMailDisabled}
          onSendCardCompanyMail={sendMail}
          isBookmarked={relatedIsBookmark}
          onToggleBookmark={handleToggleBookmark}
          canOpenAiAssistant={canOpenAiAssistant}
          isAiAssistantOpen={isAiAssistantOpen}
          onToggleAiAssistant={handleToggleAiAssistant}
        />
        <DocumentActions
          status={data.status}
          apvType={data.type.code}
          documentId={documentId}
          author={author}
          currentApproverOrder={data.currentApproverOrder}
          approvers={data.approvers}
          onEdit={onEdit}
          onDeleted={handleDeleted}
          onDecided={handleDecided}
          onSubmitted={handleSubmitted}
        />
        {isSelfApprovalAttendanceType(data.type.code) ? (
          <HiddenApprovalLineSpacer />
        ) : (
          <ApprovalLineSlot
            approvers={data.approvers}
            author={author}
            authoredAt={data.authoredAt}
            readers={data.readers}
            currentApproverOrder={data.currentApproverOrder}
            documentStatus={data.status}
            getMemberState={getMemberState}
          />
        )}
        <OpinionSlot
          approvers={data.approvers}
          withdrawalReason={data.withdrawalReason}
          author={data.author}
          withdrawnAt={data.updatedAt}
          authorOpinion={data.authorOpinion}
          authoredAt={data.authoredAt}
        />
        <AiSummarySlot
          documentId={documentId}
          ai={data.ai}
          content={data.content}
          // 양식 데이터(attributes)가 있으면 본문이 비어도 서버가 그걸로 요약함(근태·205 등) — 없으면 no-content 로 굳음
          subContent={
            data.attributes && Object.keys(data.attributes).length > 0
              ? data.attributes
              : null
          }
          documentStatus={data.status}
        />
        <BodySlot data={data} />
        <AttachmentsSlot attachments={data.attachments} />
        <RelatedApprovalSlot documentId={documentId} />
        <CommentSlot
          ref={commentSectionRef}
          documentId={documentId}
          referrers={data.referrers}
          additionalReferrers={data.additionalReferrers}
          security={data.security}
          onCommentCountChange={setCommentCount}
          commentDirtyRef={commentDirtyRef}
          commentRefreshRef={commentRefreshRef}
          onReferrersChanged={invalidateDocumentCaches}
        />
        <ReadersSlot
          approvers={data.approvers}
          referrers={data.referrers}
          additionalReferrers={data.additionalReferrers}
          readers={data.readers}
          canAddReferrer={canAddReferrerToDoc}
          onAddReferrer={handleOpenAddReferrer}
        />
        <BottomLabels authoredAt={data.authoredAt} security={data.security} />
      </Container>
      <ReasonInputModal
        open={isWithdrawOpen}
        onClose={handleCloseWithdraw}
        onSubmit={handleWithdraw}
        submitting={isWithdrawing}
        title={t('회수')}
        description={t(
          '회수는 아무도 결재하지 않은 품의만 가능하며, 회수 시 작성자의 이름으로 품의가 반려됩니다. 회수한 품의는 다시 상신할 수 있습니다.',
        )}
        reasonLabel={t('회수 사유')}
        reasonPlaceholder={t('회수 사유를 적어주세요.')}
      />
      <ReasonInputModal
        open={isReturnRequestOpen}
        onClose={handleCloseReturnRequest}
        onSubmit={handleRequestReturn}
        submitting={isRequestingReturn}
        title={t('반려요청')}
        description={t(
          '반려요청은 이미 누군가 결재한 품의에만 가능하며, 현재 결재자에게 반려 요청이 전달됩니다. 요청 후에도 문서는 진행중 상태로 유지됩니다.',
        )}
        reasonLabel={t('반려요청 사유')}
        reasonPlaceholder={t('반려요청 사유를 적어주세요.')}
        reasonMaxLength={RETURN_REQUEST_REASON_MAX_LENGTH}
      />
      <AddReferrerModal
        open={isAddReferrerOpen}
        onClose={handleCloseAddReferrer}
        documentId={documentId}
        onAdded={invalidateDocumentCaches}
      />
      <ImageZoomModal />
      {canOpenAiAssistant && (
        <AiAssistantHost
          open={isAiAssistantOpen}
          isMobile={isMobileViewport}
          documentId={documentId}
          canDecide={canDecideFromAssistant}
          onClose={handleCloseAiAssistant}
          onDecided={handleDecided}
          canShare={canAddReferrerToDoc}
          onShared={invalidateDocumentCaches}
        />
      )}
    </ImageZoomProvider>
  );
};

export default ApprovalView;
